// js/calendar.js — Navigation state: current view mode and selected date

/** @type {'day'|'week'|'month'|'year'} */
let currentView = 'week';

/** @type {Date} */
let currentDate = new Date();
currentDate.setHours(0, 0, 0, 0);

// ── Public state accessors ────────────────────────────────────────────────────

export function getView()        { return currentView; }
export function getDate()        { return new Date(currentDate); }

export function setView(view)    { currentView = view; }
export function setDate(date)    {
  currentDate = new Date(date);
  currentDate.setHours(0, 0, 0, 0);
}

// ── Date helpers ──────────────────────────────────────────────────────────────

/** Format a Date to YYYY-MM-DD */
export function toISODate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Parse YYYY-MM-DD to local Date (avoids UTC offset shifting day) */
export function fromISODate(str) {
  const [y, m, d] = str.split('-').map(Number);
  return new Date(y, m - 1, d);
}

/**
 * Returns { from: 'YYYY-MM-DD', to: 'YYYY-MM-DD', label: string }
 * for the current view and date.
 */
export function getCurrentRange() {
  const d = new Date(currentDate);
  switch (currentView) {
    case 'day': {
      const iso = toISODate(d);
      return { from: iso, to: iso, label: formatDay(d) };
    }
    case 'week': {
      const mon = startOfWeek(d);
      const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
      return {
        from: toISODate(mon),
        to:   toISODate(sun),
        label: `Week ${getWeekNumber(mon)} — ${shortDate(mon)} – ${shortDate(sun)}`,
      };
    }
    case 'month': {
      const first = new Date(d.getFullYear(), d.getMonth(), 1);
      const last  = new Date(d.getFullYear(), d.getMonth() + 1, 0);
      return {
        from: toISODate(first),
        to:   toISODate(last),
        label: d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }),
      };
    }
    case 'year': {
      const first = new Date(d.getFullYear(), 0, 1);
      const last  = new Date(d.getFullYear(), 11, 31);
      return {
        from: toISODate(first),
        to:   toISODate(last),
        label: String(d.getFullYear()),
      };
    }
  }
}

/** Advance currentDate by one period in the current view. */
export function goNext() {
  const d = new Date(currentDate);
  switch (currentView) {
    case 'day':   d.setDate(d.getDate() + 1); break;
    case 'week':  d.setDate(d.getDate() + 7); break;
    case 'month': d.setMonth(d.getMonth() + 1); break;
    case 'year':  d.setFullYear(d.getFullYear() + 1); break;
  }
  currentDate = d;
}

/** Retreat currentDate by one period in the current view. */
export function goPrev() {
  const d = new Date(currentDate);
  switch (currentView) {
    case 'day':   d.setDate(d.getDate() - 1); break;
    case 'week':  d.setDate(d.getDate() - 7); break;
    case 'month': d.setMonth(d.getMonth() - 1); break;
    case 'year':  d.setFullYear(d.getFullYear() - 1); break;
  }
  currentDate = d;
}

// ── Private helpers ───────────────────────────────────────────────────────────

/** Monday of the week containing d */
function startOfWeek(d) {
  const result = new Date(d);
  const day = result.getDay(); // 0=Sun
  const diff = day === 0 ? -6 : 1 - day;
  result.setDate(result.getDate() + diff);
  return result;
}

function getWeekNumber(d) {
  const oneJan = new Date(d.getFullYear(), 0, 1);
  return Math.ceil(((d - oneJan) / 86400000 + oneJan.getDay() + 1) / 7);
}

function formatDay(d) {
  return d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

function shortDate(d) {
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}
